/**
 * Verificação de integridade referencial.
 *
 * Nasceu na Fase 11 como a contrapartida honesta de uma escolha: o IndexedDB não tinha
 * chave estrangeira nenhuma, e sem o guarda do banco o guarda passava a ser esta rotina.
 *
 * **Com o PostgreSQL, quase tudo o que ela procurava virou impossível** — e saiu daqui em
 * vez de continuar sendo procurado:
 *
 * - arco exclusivo do recurso: `CHECK` (RN-057);
 * - funcionário em duas equipes: índice único parcial (RN-053);
 * - chave de unicidade sobrando ou faltando: deixou de existir como coluna — quem faz o
 *   trabalho é `CREATE UNIQUE INDEX … WHERE deleted_at IS NULL`, e índice não esquece;
 * - foto sem arquivo e arquivo órfão: o binário mora no Storage, e comparar banco com
 *   banco acusaria toda foto do sistema;
 * - alocação com recurso de outra obra: recusada pela chave composta (migration 0008).
 *
 * **Sobrou uma família só, e ela é real: a chave estrangeira não enxerga soft delete.**
 * `activities.floor_id` referencia `floors (id)` e continua satisfeita quando o pavimento
 * tem `deleted_at` preenchido — a linha existe, mas não está ativa, e a atividade some da
 * árvore da obra ainda apontando para ela. Nenhuma restrição declarativa pega isso.
 *
 * Na prática o defeito só chega por escrita que não passou por caso de uso: SQL à mão,
 * restauração, ou bug. É por isso que a varredura é sob demanda, e não ao abrir a tela.
 *
 * Tudo aqui é função pura sobre coleções já carregadas: dá para testar a detecção
 * corrompendo um objeto em memória, sem montar banco nenhum.
 */

/** O que pode estar quebrado. Cada valor tem uma regra por trás, não é categoria genérica. */
export type IntegrityIssueKind =
  | 'SCHEDULE_ENTRY_WITHOUT_ACTIVITY'
  | 'SCHEDULE_ENTRY_WITHOUT_SERVICE'
  | 'SCHEDULE_ITEM_WITHOUT_ACTIVITY'
  | 'ITEM_LABOR_WITHOUT_ITEM'
  | 'ITEM_LABOR_WITHOUT_JOB_ROLE'
  | 'ALLOCATION_WITHOUT_ACTIVITY'
  | 'ALLOCATION_WITHOUT_RESOURCE'
  | 'ACTIVITY_WITHOUT_FLOOR'
  | 'ACTIVITY_WITHOUT_DISCIPLINE';

/**
 * O que fazer com o registro quebrado.
 *
 * O reparo automático nunca apaga fisicamente: o soft delete tira o registro inconsistente
 * da operação e o mantém para a auditoria (ADR-021/105). O que exigiria escolher um valor
 * no lugar de quem cadastrou fica `MANUAL` — aparece listado e sem botão.
 */
export type IntegrityRepairKind = 'SOFT_DELETE' | 'MANUAL';

export type IntegritySeverity = 'ERROR' | 'WARNING';

export interface IntegrityFinding {
  readonly kind: IntegrityIssueKind;
  readonly severity: IntegritySeverity;
  /** Nome da tabela lógica, como a auditoria a chama. */
  readonly entityType: string;
  readonly entityId: string;
  readonly projectId?: string;
  /** Frase em português, pronta para a tela — quem opera não lê código de erro. */
  readonly detail: string;
  readonly repair: IntegrityRepairKind;
}

export interface IntegrityIssueDescriptor {
  readonly title: string;
  readonly explanation: string;
  readonly severity: IntegritySeverity;
  readonly repair: IntegrityRepairKind;
  readonly repairLabel: string;
}

export const INTEGRITY_ISSUE: Readonly<Record<IntegrityIssueKind, IntegrityIssueDescriptor>> = {
  SCHEDULE_ENTRY_WITHOUT_ACTIVITY: {
    title: 'Necessidade sem frente de serviço',
    explanation:
      'A linha do cronograma aponta para uma atividade excluída. O material continuaria sendo cobrado sem ninguém para executá-lo.',
    severity: 'ERROR',
    repair: 'SOFT_DELETE',
    repairLabel: 'Excluir a linha do cronograma',
  },
  SCHEDULE_ENTRY_WITHOUT_SERVICE: {
    title: 'Linha de compra com serviço excluído',
    explanation:
      'A linha do cronograma de materiais aponta um serviço que saiu do catálogo. O material continua sendo cobrado, mas o centro de custo dele (RN-348) deixou de existir.',
    severity: 'ERROR',
    repair: 'MANUAL',
    repairLabel: 'Corrigir manualmente',
  },
  SCHEDULE_ITEM_WITHOUT_ACTIVITY: {
    title: 'Item do cronograma sem frente de serviço',
    explanation:
      'O item do cronograma por item aponta para uma atividade excluída. Ele some da tela, mas continua entrando na leitura da obra e na prévia de cada importação.',
    severity: 'ERROR',
    repair: 'SOFT_DELETE',
    repairLabel: 'Excluir o item do cronograma',
  },
  ITEM_LABOR_WITHOUT_ITEM: {
    title: 'Mão de obra sem item do cronograma',
    explanation:
      'A linha de HH por função aponta um item do cronograma excluído. As horas continuariam somando no histograma da obra sem serviço nenhum para executá-las.',
    severity: 'ERROR',
    repair: 'SOFT_DELETE',
    repairLabel: 'Excluir a linha de HH',
  },
  ITEM_LABOR_WITHOUT_JOB_ROLE: {
    title: 'Mão de obra sem função',
    explanation:
      'A linha de HH aponta uma função excluída do cadastro. As horas existem, mas ninguém sabe de quem são.',
    severity: 'WARNING',
    repair: 'MANUAL',
    repairLabel: 'Corrigir manualmente',
  },
  ALLOCATION_WITHOUT_ACTIVITY: {
    title: 'Alocação sem frente de serviço',
    explanation:
      'A alocação aponta para uma atividade excluída. As horas aparecem no total da obra sem aparecer em nenhuma disciplina.',
    severity: 'ERROR',
    repair: 'SOFT_DELETE',
    repairLabel: 'Excluir a alocação',
  },
  ALLOCATION_WITHOUT_RESOURCE: {
    title: 'Alocação sem recurso',
    explanation:
      'A alocação aponta para um recurso excluído. Ninguém executa essas horas, e a capacidade do dia é conferida contra alguém que não está mais lá.',
    severity: 'ERROR',
    repair: 'SOFT_DELETE',
    repairLabel: 'Excluir a alocação',
  },
  ACTIVITY_WITHOUT_FLOOR: {
    title: 'Atividade sem pavimento',
    explanation:
      'A atividade aponta para um pavimento excluído. Ela some da árvore da obra, mas continua recebendo alocação.',
    severity: 'ERROR',
    repair: 'MANUAL',
    repairLabel: 'Corrigir manualmente',
  },
  ACTIVITY_WITHOUT_DISCIPLINE: {
    title: 'Atividade sem disciplina',
    explanation:
      'A atividade aponta para uma disciplina excluída. O planejado × realizado por disciplina deixa de contá-la.',
    severity: 'ERROR',
    repair: 'MANUAL',
    repairLabel: 'Corrigir manualmente',
  },
};

// ── O retrato do banco que a verificação lê ─────────────────────────────────────
//
// Formas mínimas, e não as entidades completas: a verificação precisa enxergar o registro
// **excluído**, que os repositórios normalmente escondem. É o único lugar do sistema que
// olha o dado nesse nível — e olha de propósito.

export interface IntegrityRecord {
  readonly id: string;
  readonly deletedAt?: string;
}

export interface ActivityRecord extends IntegrityRecord {
  readonly projectId: string;
  readonly floorId: string;
  readonly disciplineId: string;
}

export interface ScheduleEntryRecord extends IntegrityRecord {
  readonly projectId: string;
  readonly activityId: string;
  /**
   * O serviço da linha (Fase 26, RN-340). No IndexedDB não há chave estrangeira, e no
   * servidor a chave não enxerga o soft delete: o serviço excluído só se acha aqui.
   */
  readonly serviceId?: string;
}

/** O item do cronograma por item (Fase 25): pendurado numa frente, como a linha de material. */
export interface ScheduleItemRecord extends IntegrityRecord {
  readonly projectId: string;
  readonly activityId: string;
}

/** A linha de HH por função do item (Fase 26, ADR-171). */
export interface ItemLaborRecord extends IntegrityRecord {
  readonly projectId: string;
  readonly scheduleItemId: string;
  /** Ausente no serviço de terceiros (D9): ele não aponta função, e não tem com que ficar órfão. */
  readonly jobRoleId?: string;
}

export interface AllocationRecord extends IntegrityRecord {
  readonly projectId: string;
  readonly resourceId: string;
  readonly activityId: string;
  readonly date: string;
}

export interface IntegritySnapshot {
  readonly activities: readonly ActivityRecord[];
  readonly scheduleEntries: readonly ScheduleEntryRecord[];
  readonly scheduleItems: readonly ScheduleItemRecord[];
  readonly itemLabor: readonly ItemLaborRecord[];
  readonly allocations: readonly AllocationRecord[];
  /**
   * Os três conjuntos abaixo trazem só o que está **ativo**.
   *
   * É aí que mora o defeito que sobrou: a chave estrangeira garante que a linha existe, e
   * a pergunta daqui é outra — ela ainda vale? Excluído não conta como existente.
   */
  readonly floorIds: ReadonlySet<string>;
  readonly disciplineIds: ReadonlySet<string>;
  readonly resourceIds: ReadonlySet<string>;
  /** Os serviços vivos do catálogo (Fase 26) — a referência da linha de compra. */
  readonly serviceIds: ReadonlySet<string>;
  /** As funções vivas do cadastro (Fase 26) — a referência da linha de HH. */
  readonly jobRoleIds: ReadonlySet<string>;
}

export interface IntegrityReport {
  readonly findings: readonly IntegrityFinding[];
  /** Quantos registros foram examinados — a tela precisa distinguir "íntegro" de "vazio". */
  readonly checkedRecords: number;
  readonly errorCount: number;
  readonly warningCount: number;
}

function activeIds(records: readonly IntegrityRecord[]): Set<string> {
  const ids = new Set<string>();
  for (const record of records) {
    if (record.deletedAt === undefined) ids.add(record.id);
  }
  return ids;
}

function finding(
  kind: IntegrityIssueKind,
  entityType: string,
  entityId: string,
  detail: string,
  projectId?: string,
): IntegrityFinding {
  const descriptor = INTEGRITY_ISSUE[kind];
  return {
    kind,
    severity: descriptor.severity,
    repair: descriptor.repair,
    entityType,
    entityId,
    detail,
    ...(projectId === undefined ? {} : { projectId }),
  };
}

/**
 * Percorre o retrato e devolve tudo o que está inconsistente.
 *
 * Registro já excluído não é verificado: uma alocação excluída apontando para atividade
 * excluída é história, não problema.
 */
export function checkIntegrity(snapshot: IntegritySnapshot): IntegrityReport {
  const findings: IntegrityFinding[] = [];
  const liveActivities = activeIds(snapshot.activities);

  for (const activity of snapshot.activities) {
    if (activity.deletedAt !== undefined) continue;

    if (!snapshot.floorIds.has(activity.floorId)) {
      findings.push(
        finding(
          'ACTIVITY_WITHOUT_FLOOR',
          'Activity',
          activity.id,
          `Pavimento ${activity.floorId} não está ativo.`,
          activity.projectId,
        ),
      );
    }
    if (!snapshot.disciplineIds.has(activity.disciplineId)) {
      findings.push(
        finding(
          'ACTIVITY_WITHOUT_DISCIPLINE',
          'Activity',
          activity.id,
          `Disciplina ${activity.disciplineId} não está ativa.`,
          activity.projectId,
        ),
      );
    }
  }

  for (const entry of snapshot.scheduleEntries) {
    if (entry.deletedAt !== undefined) continue;

    if (!liveActivities.has(entry.activityId)) {
      findings.push(
        finding(
          'SCHEDULE_ENTRY_WITHOUT_ACTIVITY',
          'ScheduleEntry',
          entry.id,
          `Atividade ${entry.activityId} não está ativa.`,
          entry.projectId,
        ),
      );
    }

    if (entry.serviceId !== undefined && !snapshot.serviceIds.has(entry.serviceId)) {
      findings.push(
        finding(
          'SCHEDULE_ENTRY_WITHOUT_SERVICE',
          'ScheduleEntry',
          entry.id,
          `Serviço ${entry.serviceId} não está ativo.`,
          entry.projectId,
        ),
      );
    }
  }

  for (const item of snapshot.scheduleItems) {
    if (item.deletedAt !== undefined) continue;

    if (!liveActivities.has(item.activityId)) {
      findings.push(
        finding(
          'SCHEDULE_ITEM_WITHOUT_ACTIVITY',
          'ScheduleItem',
          item.id,
          `Atividade ${item.activityId} não está ativa.`,
          item.projectId,
        ),
      );
    }
  }

  const liveItems = activeIds(snapshot.scheduleItems);
  for (const line of snapshot.itemLabor) {
    if (line.deletedAt !== undefined) continue;

    if (!liveItems.has(line.scheduleItemId)) {
      findings.push(
        finding(
          'ITEM_LABOR_WITHOUT_ITEM',
          'ScheduleItemLabor',
          line.id,
          `Item ${line.scheduleItemId} não está ativo.`,
          line.projectId,
        ),
      );
    }

    if (line.jobRoleId !== undefined && !snapshot.jobRoleIds.has(line.jobRoleId)) {
      findings.push(
        finding(
          'ITEM_LABOR_WITHOUT_JOB_ROLE',
          'ScheduleItemLabor',
          line.id,
          `Função ${line.jobRoleId} não está ativa.`,
          line.projectId,
        ),
      );
    }
  }

  for (const allocation of snapshot.allocations) {
    if (allocation.deletedAt !== undefined) continue;

    if (!liveActivities.has(allocation.activityId)) {
      findings.push(
        finding(
          'ALLOCATION_WITHOUT_ACTIVITY',
          'WeeklyAllocation',
          allocation.id,
          `${allocation.date}: atividade ${allocation.activityId} não está ativa.`,
          allocation.projectId,
        ),
      );
    }

    if (!snapshot.resourceIds.has(allocation.resourceId)) {
      findings.push(
        finding(
          'ALLOCATION_WITHOUT_RESOURCE',
          'WeeklyAllocation',
          allocation.id,
          `${allocation.date}: recurso ${allocation.resourceId} não está ativo.`,
          allocation.projectId,
        ),
      );
    }
  }

  const checkedRecords =
    snapshot.activities.length +
    snapshot.scheduleEntries.length +
    snapshot.scheduleItems.length +
    snapshot.itemLabor.length +
    snapshot.allocations.length;

  return {
    findings,
    checkedRecords,
    errorCount: findings.filter((item) => item.severity === 'ERROR').length,
    warningCount: findings.filter((item) => item.severity === 'WARNING').length,
  };
}

/** Agrupa por tipo de problema — é como a tela apresenta, e como o relatório resume. */
export function groupFindings(
  findings: readonly IntegrityFinding[],
): { kind: IntegrityIssueKind; items: readonly IntegrityFinding[] }[] {
  const groups = new Map<IntegrityIssueKind, IntegrityFinding[]>();
  for (const item of findings) {
    const list = groups.get(item.kind);
    if (list === undefined) groups.set(item.kind, [item]);
    else list.push(item);
  }
  // Erros antes de avisos; dentro de cada faixa, o problema mais frequente primeiro.
  return [...groups.entries()]
    .map(([kind, items]) => ({ kind, items }))
    .sort((a, b) => {
      const severity = INTEGRITY_ISSUE[a.kind].severity === 'ERROR' ? -1 : 1;
      const otherSeverity = INTEGRITY_ISSUE[b.kind].severity === 'ERROR' ? -1 : 1;
      if (severity !== otherSeverity) return severity - otherSeverity;
      return b.items.length - a.items.length;
    });
}

/** O reparo automático resolve este achado, ou ele precisa de gente? */
export function isRepairable(item: IntegrityFinding): boolean {
  return item.repair !== 'MANUAL';
}
